import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { triggerCalendarSync } from "@/lib/googleCalendarAutoSync";

export type TipoAusencia = "vacaciones" | "baja_medica" | "asuntos_propios" | "formacion";
export type EstadoValidacionAusencia = "pendiente" | "aprobada" | "rechazada";

export interface Ausencia {
  id: string;
  empresa_id: string;
  tecnico_id: string;
  tipo: TipoAusencia;
  fecha_inicio: string;
  fecha_fin: string;
  dias_solicitados: number;
  motivo: string | null;
  estado_validacion: EstadoValidacionAusencia;
  motivo_rechazo: string | null;
  revisada_por: string | null;
  fecha_revision: string | null;
  created_at: string;
  updated_at: string;
  tecnico?: { nombre: string; email?: string } | null;
}

export interface AusenciaDocumento {
  id: string;
  ausencia_id: string;
  nombre_archivo: string;
  url_archivo: string;
  tamano_archivo: number | null;
  created_at: string;
}

export interface VacacionesCupo {
  id: string;
  empresa_id: string;
  tecnico_id: string;
  anio: number;
  dias_totales: number;
}

interface AusenciasContextValue {
  ausencias: Ausencia[];
  cupos: VacacionesCupo[];
  loading: boolean;
  modoComputo: "laborables" | "naturales";
  refresh: () => Promise<void>;
  createAusencia: (data: Omit<Ausencia, "id" | "empresa_id" | "tecnico_id" | "dias_solicitados" | "estado_validacion" | "motivo_rechazo" | "revisada_por" | "fecha_revision" | "created_at" | "updated_at" | "tecnico">) => Promise<Ausencia | null>;
  updateAusencia: (id: string, patch: Partial<Ausencia>) => Promise<void>;
  deleteAusencia: (id: string) => Promise<void>;
  aprobarAusencia: (id: string) => Promise<void>;
  rechazarAusencia: (id: string, motivo: string) => Promise<void>;
  upsertCupo: (tecnicoId: string, anio: number, dias: number) => Promise<void>;
  getSaldo: (tecnicoId: string, anio: number) => { totales: number; consumidos: number; pendientes: number; restantes: number };
  listDocumentos: (ausenciaId: string) => Promise<AusenciaDocumento[]>;
  uploadDocumento: (ausenciaId: string, file: File) => Promise<void>;
  deleteDocumento: (doc: AusenciaDocumento) => Promise<void>;
  getSignedUrl: (path: string) => Promise<string | null>;
}

const AusenciasContext = createContext<AusenciasContextValue | undefined>(undefined);

export const AusenciasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { perfil, user } = useAuth();
  const [ausencias, setAusencias] = useState<Ausencia[]>([]);
  const [cupos, setCupos] = useState<VacacionesCupo[]>([]);
  const [loading, setLoading] = useState(false);
  const [modoComputo, setModoComputo] = useState<"laborables" | "naturales">("laborables");

  const isOffice = perfil?.rol === "admin" || perfil?.rol === "gestor";

  useEffect(() => {
    const fetchModo = async () => {
      if (!perfil?.empresa_id) return;
      const { data } = await supabase
        .from("empresas")
        .select("modo_computo_ausencias")
        .eq("id", perfil.empresa_id)
        .maybeSingle();
      if ((data as any)?.modo_computo_ausencias === "naturales") setModoComputo("naturales");
      else setModoComputo("laborables");
    };
    fetchModo();
  }, [perfil?.empresa_id]);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: ausData, error: ausErr } = await supabase
        .from("ausencias")
        .select("*")
        .order("fecha_inicio", { ascending: false });
      if (ausErr) {
        console.error("Error cargando ausencias:", ausErr);
      }
      let merged = (ausData || []) as any[];
      if (merged.length) {
        const ids = Array.from(new Set(merged.map((a) => a.tecnico_id)));
        const { data: perfs } = await supabase
          .from("perfiles")
          .select("user_id, nombre, email")
          .in("user_id", ids);
        const map = new Map((perfs || []).map((p: any) => [p.user_id, { nombre: p.nombre, email: p.email }]));
        merged = merged.map((a) => ({ ...a, tecnico: map.get(a.tecnico_id) || null }));
      }
      setAusencias(merged as Ausencia[]);

      const { data: cuposData } = await supabase.from("vacaciones_cupo").select("*");
      setCupos((cuposData || []) as VacacionesCupo[]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createAusencia: AusenciasContextValue["createAusencia"] = async (data) => {
    if (!perfil?.empresa_id || !user) return null;
    const payload = {
      ...data,
      empresa_id: perfil.empresa_id,
      tecnico_id: user.id,
    };
    const { data: created, error } = await supabase.from("ausencias").insert(payload).select().single();
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return null;
    }
    await refresh();
    toast({ title: "Ausencia solicitada", description: "Pendiente de validación" });
    return created as Ausencia;
  };

  const updateAusencia: AusenciasContextValue["updateAusencia"] = async (id, patch) => {
    const previa = ausencias.find((a) => a.id === id);
    const { error } = await supabase.from("ausencias").update(patch as any).eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    const afectaCalendario =
      "estado_validacion" in patch ||
      previa?.estado_validacion === "aprobada" ||
      "fecha_inicio" in patch ||
      "fecha_fin" in patch ||
      "tipo" in patch;
    if (afectaCalendario) triggerCalendarSync([previa?.tecnico_id]);
    await refresh();
  };

  const deleteAusencia = async (id: string) => {
    const previa = ausencias.find((a) => a.id === id);
    const { error } = await supabase.from("ausencias").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    triggerCalendarSync([previa?.tecnico_id]);
    await refresh();
  };

  const aprobarAusencia = async (id: string) => {
    if (!user) return;
    await updateAusencia(id, {
      estado_validacion: "aprobada",
      motivo_rechazo: null,
      revisada_por: user.id,
      fecha_revision: new Date().toISOString(),
    });
    toast({ title: "Ausencia aprobada" });
  };

  const rechazarAusencia = async (id: string, motivo: string) => {
    if (!user) return;
    await updateAusencia(id, {
      estado_validacion: "rechazada",
      motivo_rechazo: motivo,
      revisada_por: user.id,
      fecha_revision: new Date().toISOString(),
    });
    toast({ title: "Ausencia rechazada" });
  };

  const upsertCupo = async (tecnicoId: string, anio: number, dias: number) => {
    if (!perfil?.empresa_id) return;
    const { error } = await supabase
      .from("vacaciones_cupo")
      .upsert(
        { tecnico_id: tecnicoId, anio, dias_totales: dias, empresa_id: perfil.empresa_id },
        { onConflict: "tecnico_id,anio" }
      );
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    await refresh();
  };

  const getSaldo = (tecnicoId: string, anio: number) => {
    const cupo = cupos.find((c) => c.tecnico_id === tecnicoId && c.anio === anio);
    const defaultDias = modoComputo === "naturales" ? 30 : 22;
    const totales = Number(cupo?.dias_totales ?? defaultDias);
    const ausYear = ausencias.filter(
      (a) => a.tecnico_id === tecnicoId && a.tipo === "vacaciones" && new Date(a.fecha_inicio).getFullYear() === anio
    );
    const consumidos = ausYear.filter((a) => a.estado_validacion === "aprobada").reduce((s, a) => s + Number(a.dias_solicitados), 0);
    const pendientes = ausYear.filter((a) => a.estado_validacion === "pendiente").reduce((s, a) => s + Number(a.dias_solicitados), 0);
    return { totales, consumidos, pendientes, restantes: totales - consumidos - pendientes };
  };

  const listDocumentos = async (ausenciaId: string) => {
    const { data } = await supabase.from("ausencia_documentos").select("*").eq("ausencia_id", ausenciaId);
    return (data || []) as AusenciaDocumento[];
  };

  const uploadDocumento = async (ausenciaId: string, file: File) => {
    if (!user) return;
    const path = `${user.id}/${ausenciaId}/${Date.now()}-${file.name}`;
    const { error: upErr } = await supabase.storage.from("ausencia-documentos").upload(path, file);
    if (upErr) {
      toast({ title: "Error subiendo archivo", description: upErr.message, variant: "destructive" });
      return;
    }
    const { error: insErr } = await supabase.from("ausencia_documentos").insert({
      ausencia_id: ausenciaId,
      nombre_archivo: file.name,
      url_archivo: path,
      tamano_archivo: file.size,
    });
    if (insErr) {
      toast({ title: "Error", description: insErr.message, variant: "destructive" });
    }
  };

  const deleteDocumento = async (doc: AusenciaDocumento) => {
    await supabase.storage.from("ausencia-documentos").remove([doc.url_archivo]);
    await supabase.from("ausencia_documentos").delete().eq("id", doc.id);
  };

  const getSignedUrl = async (path: string) => {
    const { data } = await supabase.storage.from("ausencia-documentos").createSignedUrl(path, 3600);
    return data?.signedUrl ?? null;
  };

  return (
    <AusenciasContext.Provider
      value={{
        ausencias,
        cupos,
        loading,
        modoComputo,
        refresh,
        createAusencia,
        updateAusencia,
        deleteAusencia,
        aprobarAusencia,
        rechazarAusencia,
        upsertCupo,
        getSaldo,
        listDocumentos,
        uploadDocumento,
        deleteDocumento,
        getSignedUrl,
      }}
    >
      {children}
    </AusenciasContext.Provider>
  );
};

export const useAusencias = () => {
  const ctx = useContext(AusenciasContext);
  if (!ctx) throw new Error("useAusencias debe usarse dentro de AusenciasProvider");
  return ctx;
};

export const TIPO_AUSENCIA_LABEL: Record<TipoAusencia, string> = {
  vacaciones: "Vacaciones",
  baja_medica: "Baja médica",
  asuntos_propios: "Asuntos propios",
  formacion: "Formación",
};

export const TIPO_AUSENCIA_COLOR: Record<TipoAusencia, string> = {
  vacaciones: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  baja_medica: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  asuntos_propios: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  formacion: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
};
